import { z } from 'zod';
import { OrgId, UnixTs, UserId } from '../types/branded';
import { GradeLevel, SchoolDivision } from '../types/enums';

export const CampProgramTypeSchema = z.enum([
  'summer_day',
  'specialty',
  'sports',
  'academic',
  'other',
]);
export type CampProgramType = z.infer<typeof CampProgramTypeSchema>;

export const CampProgramStatusSchema = z.enum(['draft', 'published', 'archived']);
export type CampProgramStatus = z.infer<typeof CampProgramStatusSchema>;

export const CampGenderEligibilitySchema = z.enum(['all', 'boys', 'girls']);
export type CampGenderEligibility = z.infer<typeof CampGenderEligibilitySchema>;

export const CampRegistrationStatusSchema = z.enum([
  'draft',
  'pending_payment',
  'registered',
  'waitlisted',
  'cancelled',
  'refund_initiated',
  'refunded',
]);
export type CampRegistrationStatus = z.infer<typeof CampRegistrationStatusSchema>;

export const CampPaymentStatusSchema = z.enum([
  'unpaid',
  'pending',
  'paid',
  'partially_paid',
  'refunded',
  'waived',
]);
export type CampPaymentStatus = z.infer<typeof CampPaymentStatusSchema>;

export const CampWaitlistStatusSchema = z.enum(['active', 'promoted', 'expired', 'removed']);
export type CampWaitlistStatus = z.infer<typeof CampWaitlistStatusSchema>;

export const CampIngestStateSchema = z.enum(['unknown', 'healthy', 'stale', 'failed', 'syncing']);
export type CampIngestState = z.infer<typeof CampIngestStateSchema>;

export const CampSourceSystemSchema = z.enum(['gravity_forms', 'ystnj_native', 'hybrid']);
export type CampSourceSystem = z.infer<typeof CampSourceSystemSchema>;
export const CampLegacyAuthoritySchema = z.enum(['gf_export', 'google_sheet', 'ystnj_native']);
export type CampLegacyAuthority = z.infer<typeof CampLegacyAuthoritySchema>;
export const CampParityStatusSchema = z.enum(['unknown', 'healthy', 'stale', 'failed']);
export type CampParityStatus = z.infer<typeof CampParityStatusSchema>;

export const CampRosterExportFormatSchema = z.enum(['csv', 'pdf']);
export type CampRosterExportFormat = z.infer<typeof CampRosterExportFormatSchema>;

export const CampCommunicationStatusSchema = z.enum(['queued', 'sent', 'cancelled']);
export type CampCommunicationStatus = z.infer<typeof CampCommunicationStatusSchema>;

export const CampCrosswalkLinkStatusSchema = z.enum(['linked', 'ambiguous', 'orphaned', 'stale']);
export type CampCrosswalkLinkStatus = z.infer<typeof CampCrosswalkLinkStatusSchema>;

export const CampCrosswalkMatchStrategySchema = z.enum([
  'none',
  'parent_email_exact',
  'student_exact_name_dob',
  'candidate_suggestions',
  'manual_confirmed',
  'manual_rejected',
  'manual_cleared',
]);
export type CampCrosswalkMatchStrategy = z.infer<typeof CampCrosswalkMatchStrategySchema>;

export const CampCrosswalkMatchCandidateSchema = z.object({
  entityType: z.enum(['family', 'student']),
  canonicalId: z.string().min(1),
  familyId: z.string().min(1).nullable().optional().default(null),
  displayName: z.string().min(1),
  childAppId: z.string().min(1).nullable().optional().default(null),
  applicantId: z.string().min(1).nullable().optional().default(null),
  dateOfBirth: z.string().min(10).nullable().optional().default(null),
  email: z.string().email().nullable().optional().default(null),
  matchReasons: z.array(z.string().min(1)).default([]),
}).strict();
export type CampCrosswalkMatchCandidate = z.infer<typeof CampCrosswalkMatchCandidateSchema>;

export const CampShadowFamilySchema = z.object({
  id: z.string().uuid(),
  tenantId: OrgId,
  sourceSystem: CampSourceSystemSchema,
  externalFamilyKey: z.string().min(1),
  parentExternalEntryId: z.string().min(1).nullable().optional().default(null),
  familyName: z.string().min(1),
  parentFirstName: z.string().min(1).nullable().optional().default(null),
  parentLastName: z.string().min(1).nullable().optional().default(null),
  parentEmail: z.string().email().nullable().optional().default(null),
  linkedFamilyId: z.string().min(1).nullable().optional().default(null),
  linkStatus: CampCrosswalkLinkStatusSchema,
  sourceSnapshotDate: z.string().min(10).nullable().optional().default(null),
  createdAt: UnixTs,
  updatedAt: UnixTs,
}).strict();
export type CampShadowFamily = z.infer<typeof CampShadowFamilySchema>;

export const CampShadowStudentSchema = z.object({
  id: z.string().uuid(),
  tenantId: OrgId,
  shadowFamilyId: z.string().uuid(),
  sourceSystem: CampSourceSystemSchema,
  externalEntryId: z.string().min(1),
  parentExternalEntryId: z.string().min(1).nullable().optional().default(null),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  preferredName: z.string().min(1).nullable().optional().default(null),
  dateOfBirth: z.string().min(10).nullable().optional().default(null),
  gender: z.string().min(1).nullable().optional().default(null),
  grade: GradeLevel,
  linkedFamilyId: z.string().min(1).nullable().optional().default(null),
  linkedStudentId: z.string().min(1).nullable().optional().default(null),
  linkStatus: CampCrosswalkLinkStatusSchema,
  sourceSnapshotDate: z.string().min(10).nullable().optional().default(null),
  createdAt: UnixTs,
  updatedAt: UnixTs,
}).strict();
export type CampShadowStudent = z.infer<typeof CampShadowStudentSchema>;

export const CampFamilyCrosswalkSchema = z.object({
  tenantId: OrgId,
  shadowFamilyId: z.string().uuid(),
  linkedFamilyId: z.string().min(1).nullable().optional().default(null),
  status: CampCrosswalkLinkStatusSchema,
  candidates: z.array(CampCrosswalkMatchCandidateSchema),
  matchStrategy: CampCrosswalkMatchStrategySchema,
  reviewedBy: z.string().min(1).nullable().optional().default(null),
  createdAt: UnixTs,
  updatedAt: UnixTs,
}).strict();
export type CampFamilyCrosswalk = z.infer<typeof CampFamilyCrosswalkSchema>;

export const CampStudentCrosswalkSchema = z.object({
  tenantId: OrgId,
  shadowStudentId: z.string().uuid(),
  shadowFamilyId: z.string().uuid(),
  linkedFamilyId: z.string().min(1).nullable().optional().default(null),
  linkedStudentId: z.string().min(1).nullable().optional().default(null),
  status: CampCrosswalkLinkStatusSchema,
  candidates: z.array(CampCrosswalkMatchCandidateSchema),
  matchStrategy: CampCrosswalkMatchStrategySchema,
  reviewedBy: z.string().min(1).nullable().optional().default(null),
  createdAt: UnixTs,
  updatedAt: UnixTs,
}).strict();
export type CampStudentCrosswalk = z.infer<typeof CampStudentCrosswalkSchema>;

export const CampSessionSchema = z.object({
  id: z.string().uuid(),
  tenantId: OrgId,
  programId: z.string().uuid(),
  name: z.string().min(1).nullable().optional(),
  startDate: z.string().min(10),
  endDate: z.string().min(10),
  dailyStartTime: z.string().min(1),
  dailyEndTime: z.string().min(1),
  capacity: z.number().int().nonnegative(),
  enrolledCount: z.number().int().nonnegative(),
  waitlistCount: z.number().int().nonnegative(),
  sourceSystem: CampSourceSystemSchema.default('ystnj_native'),
  externalSourceKey: z.string().min(1).nullable().optional().default(null),
  sourceSnapshotDate: z.string().min(10).nullable().optional().default(null),
  createdAt: UnixTs,
  updatedAt: UnixTs,
}).strict();
export type CampSession = z.infer<typeof CampSessionSchema>;

export const CampProgramSchema = z.object({
  id: z.string().uuid(),
  tenantId: OrgId,
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  campType: CampProgramTypeSchema,
  location: z.string().nullable().optional(),
  minGrade: GradeLevel,
  maxGrade: GradeLevel,
  genderEligibility: CampGenderEligibilitySchema,
  pricePerSessionCents: z.number().int().nonnegative(),
  siblingDiscountCents: z.number().int().nonnegative().nullable().optional(),
  multiSessionDiscountPct: z.number().min(0).max(100).nullable().optional(),
  registrationOpen: z.string().min(10),
  registrationClose: z.string().min(10),
  cancelFullRefundBefore: z.string().min(10).nullable().optional(),
  cancelPartialRefundBefore: z.string().min(10).nullable().optional(),
  cancelPartialRefundPct: z.number().min(0).max(100).nullable().optional(),
  packingList: z.string().nullable().optional(),
  waiverText: z.string().nullable().optional(),
  status: CampProgramStatusSchema,
  sourceSystem: CampSourceSystemSchema.default('ystnj_native'),
  externalSourceKey: z.string().min(1).nullable().optional().default(null),
  sourceSnapshotDate: z.string().min(10).nullable().optional().default(null),
  createdBy: UserId,
  createdAt: UnixTs,
  updatedAt: UnixTs,
}).strict();
export type CampProgram = z.infer<typeof CampProgramSchema>;

export const CampEligibleStudentSchema = z.object({
  studentId: z.string().min(1),
  familyId: z.string().min(1),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  preferredName: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  grade: GradeLevel,
  division: SchoolDivision,
}).strict();
export type CampEligibleStudent = z.infer<typeof CampEligibleStudentSchema>;

export const CampRegistrationSelectionSchema = z.object({
  studentId: z.string().min(1),
  studentName: z.string().min(1),
  sessionIds: z.array(z.string().uuid()).min(1),
  priceCents: z.number().int().nonnegative(),
}).strict();
export type CampRegistrationSelection = z.infer<typeof CampRegistrationSelectionSchema>;

export const CampPricingSummarySchema = z.object({
  subtotalCents: z.number().int().nonnegative(),
  discountCents: z.number().int().nonnegative(),
  totalCents: z.number().int().nonnegative(),
  amountDueCents: z.number().int().nonnegative(),
}).strict();
export type CampPricingSummary = z.infer<typeof CampPricingSummarySchema>;

export const CampPaymentReadinessSchema = z.object({
  ready: z.boolean(),
  moduleId: z.literal('camps'),
  reason: z.string().nullable().optional(),
}).strict();
export type CampPaymentReadiness = z.infer<typeof CampPaymentReadinessSchema>;

export const CampRegistrationDraftSchema = z.object({
  id: z.string().uuid(),
  tenantId: OrgId,
  familyId: z.string().min(1),
  familyName: z.string().min(1),
  programId: z.string().uuid(),
  selections: z.array(CampRegistrationSelectionSchema).min(1),
  pricingSummary: CampPricingSummarySchema,
  waiverAccepted: z.boolean(),
  sourceSystem: CampSourceSystemSchema,
  externalEntryId: z.string().min(1).nullable().optional(),
  createdAt: UnixTs,
  updatedAt: UnixTs,
}).strict();
export type CampRegistrationDraft = z.infer<typeof CampRegistrationDraftSchema>;

export const CampRegistrationSchema = z.object({
  id: z.string().uuid(),
  tenantId: OrgId,
  familyId: z.string().min(1),
  familyName: z.string().min(1),
  programId: z.string().uuid(),
  programName: z.string().min(1),
  sessionIds: z.array(z.string().uuid()).min(1),
  studentId: z.string().min(1),
  studentName: z.string().min(1),
  status: CampRegistrationStatusSchema,
  paymentStatus: CampPaymentStatusSchema,
  grossCents: z.number().int().nonnegative(),
  amountPaidCents: z.number().int().nonnegative(),
  refundAmountCents: z.number().int().nonnegative(),
  sourceSystem: CampSourceSystemSchema,
  externalEntryId: z.string().min(1).nullable().optional(),
  parentExternalEntryId: z.string().min(1).nullable().optional().default(null),
  sourceSnapshotDate: z.string().min(10).nullable().optional().default(null),
  linkedFamilyId: z.string().min(1).nullable().optional().default(null),
  linkedStudentId: z.string().min(1).nullable().optional().default(null),
  stripePaymentIntentId: z.string().min(1).nullable().optional(),
  stripeAccountId: z.string().min(1).nullable().optional(),
  cancellationReason: z.string().nullable().optional(),
  refundReason: z.string().nullable().optional(),
  createdAt: UnixTs,
  updatedAt: UnixTs,
  cancelledAt: UnixTs.nullable().optional(),
}).strict();
export type CampRegistration = z.infer<typeof CampRegistrationSchema>;

export const CampWaitlistEntrySchema = z.object({
  id: z.string().uuid(),
  tenantId: OrgId,
  familyId: z.string().min(1),
  familyName: z.string().min(1),
  programId: z.string().uuid(),
  sessionId: z.string().uuid(),
  studentId: z.string().min(1),
  studentName: z.string().min(1),
  position: z.number().int().positive(),
  status: CampWaitlistStatusSchema,
  createdAt: UnixTs,
  updatedAt: UnixTs,
  expiresAt: UnixTs.nullable().optional(),
}).strict();
export type CampWaitlistEntry = z.infer<typeof CampWaitlistEntrySchema>;

export const CampIngestStatusSchema = z.object({
  tenantId: OrgId,
  state: CampIngestStateSchema,
  sourceSystem: CampSourceSystemSchema,
  legacyAuthority: CampLegacyAuthoritySchema.default('gf_export'),
  lastRefreshAt: UnixTs.nullable().optional(),
  lastSuccessAt: UnixTs.nullable().optional(),
  lastFailureAt: UnixTs.nullable().optional(),
  lastGfExportAt: UnixTs.nullable().optional().default(null),
  lastSheetSyncAt: UnixTs.nullable().optional().default(null),
  lastSheetMirrorAt: UnixTs.nullable().optional().default(null),
  snapshotDate: z.string().min(10).nullable().optional(),
  lastS3Prefix: z.string().min(1).nullable().optional().default(null),
  lastRequestEventId: z.string().min(1).nullable().optional().default(null),
  authoritativeSpreadsheetId: z.string().min(1).nullable().optional().default(null),
  authoritativeApplicationsTab: z.string().min(1).nullable().optional().default(null),
  authoritativeCampersTab: z.string().min(1).nullable().optional().default(null),
  lastImportedProgramCount: z.number().int().nonnegative().optional().default(0),
  lastImportedSessionCount: z.number().int().nonnegative().optional().default(0),
  lastImportedRegistrationCount: z.number().int().nonnegative().optional().default(0),
  lastPaymentFollowupCount: z.number().int().nonnegative().optional().default(0),
  parityStatus: CampParityStatusSchema.default('unknown'),
  parityMessage: z.string().nullable().optional().default(null),
  message: z.string().nullable().optional(),
}).strict();
export type CampIngestStatus = z.infer<typeof CampIngestStatusSchema>;

export const CampRosterExportRecordSchema = z.object({
  id: z.string().uuid(),
  tenantId: OrgId,
  programId: z.string().uuid(),
  format: CampRosterExportFormatSchema,
  status: z.enum(['queued', 'completed']),
  requestedBy: UserId,
  requestedAt: UnixTs,
}).strict();
export type CampRosterExportRecord = z.infer<typeof CampRosterExportRecordSchema>;

export const CampCommunicationRequestSchema = z.object({
  id: z.string().uuid(),
  tenantId: OrgId,
  programId: z.string().uuid(),
  audience: z.enum(['registered', 'waitlist', 'all']),
  templateKey: z.string().min(1),
  status: CampCommunicationStatusSchema,
  requestedBy: UserId,
  requestedAt: UnixTs,
}).strict();
export type CampCommunicationRequest = z.infer<typeof CampCommunicationRequestSchema>;

export const CampProgramViewSchema = z.object({
  program: CampProgramSchema,
  sessions: z.array(CampSessionSchema),
  spotsRemaining: z.number().int().nonnegative(),
  waitlistOnly: z.boolean(),
  isRegistered: z.boolean(),
  registrationClosed: z.boolean(),
}).strict();
export type CampProgramView = z.infer<typeof CampProgramViewSchema>;

export const CampRegistrationSummarySchema = z.object({
  registeredCount: z.number().int().nonnegative(),
  waitlistedCount: z.number().int().nonnegative(),
  openRegistrationCount: z.number().int().nonnegative(),
  amountDueCents: z.number().int().nonnegative(),
}).strict();
export type CampRegistrationSummary = z.infer<typeof CampRegistrationSummarySchema>;

export const ParentCampCatalogResponseSchema = z.object({
  familyId: z.string().min(1),
  familyName: z.string().min(1),
  eligibleStudents: z.array(CampEligibleStudentSchema),
  registrationSummary: CampRegistrationSummarySchema,
  items: z.array(CampProgramViewSchema),
}).strict();
export type ParentCampCatalogResponse = z.infer<typeof ParentCampCatalogResponseSchema>;

export const ParentCampRegistrationResponseSchema = z.object({
  familyId: z.string().min(1),
  familyName: z.string().min(1),
  program: CampProgramSchema,
  sessions: z.array(CampSessionSchema),
  eligibleStudents: z.array(CampEligibleStudentSchema),
  draft: CampRegistrationDraftSchema.nullable(),
  pricingSummary: CampPricingSummarySchema,
  paymentReadiness: CampPaymentReadinessSchema,
}).strict();
export type ParentCampRegistrationResponse = z.infer<typeof ParentCampRegistrationResponseSchema>;

export const CampWaitlistListResponseSchema = z.object({
  items: z.array(CampWaitlistEntrySchema),
  nextToken: z.string().nullable().optional(),
  totalCount: z.number().int().nonnegative(),
}).strict();
export type CampWaitlistListResponse = z.infer<typeof CampWaitlistListResponseSchema>;

export const CampRegistrationListResponseSchema = z.object({
  items: z.array(CampRegistrationSchema),
  nextToken: z.string().nullable().optional(),
  totalCount: z.number().int().nonnegative(),
}).strict();
export type CampRegistrationListResponse = z.infer<typeof CampRegistrationListResponseSchema>;

export const CampDashboardSummarySchema = z.object({
  activeProgramsCount: z.number().int().nonnegative(),
  enrolledCampersCount: z.number().int().nonnegative(),
  openSeatsCount: z.number().int().nonnegative(),
  waitlistCount: z.number().int().nonnegative(),
  unpaidBalanceCents: z.number().int().nonnegative(),
}).strict();
export type CampDashboardSummary = z.infer<typeof CampDashboardSummarySchema>;

export const CampWaitlistPressureSummarySchema = z.object({
  totalWaitlisted: z.number().int().nonnegative(),
  sessionsAtCapacity: z.number().int().nonnegative(),
}).strict();
export type CampWaitlistPressureSummary = z.infer<typeof CampWaitlistPressureSummarySchema>;

export const CampRosterReadinessSummarySchema = z.object({
  readyPrograms: z.number().int().nonnegative(),
  pendingPrograms: z.number().int().nonnegative(),
}).strict();
export type CampRosterReadinessSummary = z.infer<typeof CampRosterReadinessSummarySchema>;

export const CampOpsTelemetrySchema = z.object({
  latestRefreshAt: UnixTs.nullable().optional(),
  lastSnapshotDate: z.string().min(10).nullable().optional(),
  sourceSystem: CampSourceSystemSchema,
  legacyAuthority: CampLegacyAuthoritySchema.default('gf_export'),
  lastGfExportAt: UnixTs.nullable().optional().default(null),
  lastSheetSyncAt: UnixTs.nullable().optional().default(null),
  lastSheetMirrorAt: UnixTs.nullable().optional().default(null),
  authoritativeSpreadsheetId: z.string().min(1).nullable().optional().default(null),
  authoritativeApplicationsTab: z.string().min(1).nullable().optional().default(null),
  authoritativeCampersTab: z.string().min(1).nullable().optional().default(null),
  parityStatus: CampParityStatusSchema.default('unknown'),
  parityMessage: z.string().nullable().optional().default(null),
}).strict();
export type CampOpsTelemetry = z.infer<typeof CampOpsTelemetrySchema>;

export const CampEventResponseSummarySchema = z.object({
  pendingCommunications: z.number().int().nonnegative(),
  pendingWaitlistPromotions: z.number().int().nonnegative(),
}).strict();
export type CampEventResponseSummary = z.infer<typeof CampEventResponseSummarySchema>;

export const SchoolCampDashboardResponseSchema = z.object({
  summary: CampDashboardSummarySchema,
  items: z.array(CampProgramViewSchema),
  ingestHealth: CampIngestStatusSchema,
  waitlistPressure: CampWaitlistPressureSummarySchema,
  rosterReadiness: CampRosterReadinessSummarySchema,
  opsTelemetry: CampOpsTelemetrySchema,
  eventResponseSummary: CampEventResponseSummarySchema,
}).strict();
export type SchoolCampDashboardResponse = z.infer<typeof SchoolCampDashboardResponseSchema>;

export const CampFinanceSummarySchema = z.object({
  collectedCents: z.number().int().nonnegative(),
  outstandingCents: z.number().int().nonnegative(),
  refundedCents: z.number().int().nonnegative(),
}).strict();
export type CampFinanceSummary = z.infer<typeof CampFinanceSummarySchema>;

export const CampCommunicationsSummarySchema = z.object({
  lastSentAt: UnixTs.nullable().optional(),
  pendingCount: z.number().int().nonnegative(),
}).strict();
export type CampCommunicationsSummary = z.infer<typeof CampCommunicationsSummarySchema>;

export const CampExportAuditSchema = z.object({
  lastExportAt: UnixTs.nullable().optional(),
  lastExportFormat: CampRosterExportFormatSchema.nullable().optional(),
  lastExportStatus: z.string().nullable().optional(),
}).strict();
export type CampExportAudit = z.infer<typeof CampExportAuditSchema>;

export const SchoolCampProgramDetailResponseSchema = z.object({
  program: CampProgramSchema,
  sessions: z.array(CampSessionSchema),
  registrations: CampRegistrationListResponseSchema,
  waitlist: CampWaitlistListResponseSchema,
  financeSummary: CampFinanceSummarySchema,
  communicationsSummary: CampCommunicationsSummarySchema,
  ingestHealth: CampIngestStatusSchema,
  exportAudit: CampExportAuditSchema,
}).strict();
export type SchoolCampProgramDetailResponse = z.infer<typeof SchoolCampProgramDetailResponseSchema>;

export const CampProgramEditorDefaultsSchema = z.object({
  gradeOptions: z.array(GradeLevel),
  genderEligibilityOptions: z.array(CampGenderEligibilitySchema),
  campTypeOptions: z.array(CampProgramTypeSchema),
}).strict();
export type CampProgramEditorDefaults = z.infer<typeof CampProgramEditorDefaultsSchema>;

export const CampProgramEditorResponseSchema = z.object({
  program: CampProgramSchema.nullable(),
  sessions: z.array(CampSessionSchema),
  defaults: CampProgramEditorDefaultsSchema,
  ingestHealth: CampIngestStatusSchema,
}).strict();
export type CampProgramEditorResponse = z.infer<typeof CampProgramEditorResponseSchema>;

export const CampImportCrosswalkStudentDetailSchema = z.object({
  shadowStudent: CampShadowStudentSchema,
  crosswalk: CampStudentCrosswalkSchema,
}).strict();
export type CampImportCrosswalkStudentDetail = z.infer<typeof CampImportCrosswalkStudentDetailSchema>;

export const CampImportCrosswalkQueueItemSchema = z.object({
  itemType: z.enum(['family', 'student']),
  shadowFamilyId: z.string().uuid(),
  shadowStudentId: z.string().uuid().nullable().optional().default(null),
  displayName: z.string().min(1),
  status: CampCrosswalkLinkStatusSchema,
  linkedFamilyId: z.string().min(1).nullable().optional().default(null),
  linkedStudentId: z.string().min(1).nullable().optional().default(null),
  candidateCount: z.number().int().nonnegative(),
  familyName: z.string().min(1).nullable().optional().default(null),
  studentName: z.string().min(1).nullable().optional().default(null),
  primaryEmail: z.string().email().nullable().optional().default(null),
  dateOfBirth: z.string().min(10).nullable().optional().default(null),
  sourceSnapshotDate: z.string().min(10).nullable().optional().default(null),
  updatedAt: UnixTs,
}).strict();
export type CampImportCrosswalkQueueItem = z.infer<typeof CampImportCrosswalkQueueItemSchema>;

export const CampImportCrosswalkQueueResponseSchema = z.object({
  items: z.array(CampImportCrosswalkQueueItemSchema),
  nextToken: z.string().nullable().optional(),
  totalCount: z.number().int().nonnegative(),
}).strict();
export type CampImportCrosswalkQueueResponse = z.infer<typeof CampImportCrosswalkQueueResponseSchema>;

export const CampImportCrosswalkDetailSchema = z.object({
  family: CampShadowFamilySchema,
  familyCrosswalk: CampFamilyCrosswalkSchema,
  students: z.array(CampImportCrosswalkStudentDetailSchema),
}).strict();
export type CampImportCrosswalkDetail = z.infer<typeof CampImportCrosswalkDetailSchema>;
export const CampImportCrosswalkDetailResponseSchema = CampImportCrosswalkDetailSchema;
export type CampImportCrosswalkDetailResponse = CampImportCrosswalkDetail;

export const CampProgramCreateInputSchema = CampProgramSchema.omit({
  id: true,
  tenantId: true,
  createdBy: true,
  createdAt: true,
  updatedAt: true,
});
export type CampProgramCreateInput = z.infer<typeof CampProgramCreateInputSchema>;

export const CampProgramUpdateInputSchema = CampProgramCreateInputSchema.partial();
export type CampProgramUpdateInput = z.infer<typeof CampProgramUpdateInputSchema>;

export const CampSessionInputSchema = CampSessionSchema.omit({
  id: true,
  tenantId: true,
  programId: true,
  enrolledCount: true,
  waitlistCount: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  id: z.string().uuid().optional(),
});
export type CampSessionInput = z.infer<typeof CampSessionInputSchema>;

export const GetParentCatalogInputSchema = z.object({
  tenantId: OrgId.optional(),
}).strict();
export type GetParentCatalogInput = z.infer<typeof GetParentCatalogInputSchema>;

export const GetParentRegistrationInputSchema = z.object({
  tenantId: OrgId.optional(),
  programId: z.string().uuid(),
}).strict();
export type GetParentRegistrationInput = z.infer<typeof GetParentRegistrationInputSchema>;

export const ListParentWaitlistInputSchema = z.object({
  tenantId: OrgId.optional(),
  limit: z.number().int().positive().max(100).optional(),
  nextToken: z.string().optional(),
}).strict();
export type ListParentWaitlistInput = z.infer<typeof ListParentWaitlistInputSchema>;

export const ListParentRegistrationsInputSchema = z.object({
  tenantId: OrgId.optional(),
  limit: z.number().int().positive().max(100).optional(),
  nextToken: z.string().optional(),
}).strict();
export type ListParentRegistrationsInput = z.infer<typeof ListParentRegistrationsInputSchema>;

export const GetSchoolDashboardInputSchema = z.object({
  tenantId: OrgId.optional(),
}).strict();
export type GetSchoolDashboardInput = z.infer<typeof GetSchoolDashboardInputSchema>;

export const GetProgramInputSchema = z.object({
  tenantId: OrgId.optional(),
  programId: z.string().uuid(),
}).strict();
export type GetProgramInput = z.infer<typeof GetProgramInputSchema>;

export const GetProgramEditorInputSchema = z.object({
  tenantId: OrgId.optional(),
  programId: z.string().uuid().optional(),
}).strict();
export type GetProgramEditorInput = z.infer<typeof GetProgramEditorInputSchema>;

export const CampImportCrosswalkQueueStatusFilterSchema = z.enum([
  'linked',
  'ambiguous',
  'orphaned',
  'stale',
  'unresolved',
]);
export type CampImportCrosswalkQueueStatusFilter = z.infer<typeof CampImportCrosswalkQueueStatusFilterSchema>;

export const ListCampImportCrosswalkQueueInputSchema = z.object({
  tenantId: OrgId.optional(),
  itemType: z.enum(['family', 'student', 'all']).optional(),
  status: CampImportCrosswalkQueueStatusFilterSchema.optional(),
  limit: z.number().int().positive().max(100).optional(),
  nextToken: z.string().optional(),
}).strict();
export type ListCampImportCrosswalkQueueInput = z.infer<typeof ListCampImportCrosswalkQueueInputSchema>;

export const GetCampImportCrosswalkDetailInputSchema = z.object({
  tenantId: OrgId.optional(),
  shadowFamilyId: z.string().uuid().optional(),
  shadowStudentId: z.string().uuid().optional(),
}).strict().refine(
  (value) => Boolean(value.shadowFamilyId || value.shadowStudentId),
  { message: 'shadowFamilyId or shadowStudentId is required.' }
);
export type GetCampImportCrosswalkDetailInput = z.infer<typeof GetCampImportCrosswalkDetailInputSchema>;

export const ConfirmCampShadowFamilyLinkInputSchema = z.object({
  tenantId: OrgId.optional(),
  shadowFamilyId: z.string().uuid(),
  linkedFamilyId: z.string().min(1),
}).strict();
export type ConfirmCampShadowFamilyLinkInput = z.infer<typeof ConfirmCampShadowFamilyLinkInputSchema>;

export const ConfirmCampShadowStudentLinkInputSchema = z.object({
  tenantId: OrgId.optional(),
  shadowStudentId: z.string().uuid(),
  linkedStudentId: z.string().min(1),
}).strict();
export type ConfirmCampShadowStudentLinkInput = z.infer<typeof ConfirmCampShadowStudentLinkInputSchema>;

export const RejectCampShadowFamilyCandidatesInputSchema = z.object({
  tenantId: OrgId.optional(),
  shadowFamilyId: z.string().uuid(),
}).strict();
export type RejectCampShadowFamilyCandidatesInput = z.infer<typeof RejectCampShadowFamilyCandidatesInputSchema>;

export const RejectCampShadowStudentCandidatesInputSchema = z.object({
  tenantId: OrgId.optional(),
  shadowStudentId: z.string().uuid(),
}).strict();
export type RejectCampShadowStudentCandidatesInput = z.infer<typeof RejectCampShadowStudentCandidatesInputSchema>;

export const ClearCampCrosswalkLinkInputSchema = z.object({
  tenantId: OrgId.optional(),
  shadowFamilyId: z.string().uuid().optional(),
  shadowStudentId: z.string().uuid().optional(),
}).strict().refine(
  (value) => Boolean(value.shadowFamilyId || value.shadowStudentId),
  { message: 'shadowFamilyId or shadowStudentId is required.' }
);
export type ClearCampCrosswalkLinkInput = z.infer<typeof ClearCampCrosswalkLinkInputSchema>;

export const RecomputeCampCrosswalkCandidatesInputSchema = z.object({
  tenantId: OrgId.optional(),
  shadowFamilyId: z.string().uuid().optional(),
  shadowStudentId: z.string().uuid().optional(),
}).strict().refine(
  (value) => Boolean(value.shadowFamilyId || value.shadowStudentId),
  { message: 'shadowFamilyId or shadowStudentId is required.' }
);
export type RecomputeCampCrosswalkCandidatesInput = z.infer<typeof RecomputeCampCrosswalkCandidatesInputSchema>;

export const SaveRegistrationDraftInputSchema = z.object({
  tenantId: OrgId.optional(),
  draftId: z.string().uuid().optional(),
  programId: z.string().uuid(),
  selections: z.array(z.object({
    studentId: z.string().min(1),
    sessionIds: z.array(z.string().uuid()).min(1),
  }).strict()).min(1),
  waiverAccepted: z.boolean(),
}).strict();
export type SaveRegistrationDraftInput = z.infer<typeof SaveRegistrationDraftInputSchema>;

export const SubmitRegistrationInputSchema = z.object({
  tenantId: OrgId.optional(),
  draftId: z.string().uuid(),
}).strict();
export type SubmitRegistrationInput = z.infer<typeof SubmitRegistrationInputSchema>;

export const CancelRegistrationInputSchema = z.object({
  tenantId: OrgId.optional(),
  registrationId: z.string().uuid(),
  reason: z.string().optional(),
}).strict();
export type CancelRegistrationInput = z.infer<typeof CancelRegistrationInputSchema>;

export const JoinWaitlistInputSchema = z.object({
  tenantId: OrgId.optional(),
  programId: z.string().uuid(),
  sessionId: z.string().uuid(),
  studentId: z.string().min(1),
}).strict();
export type JoinWaitlistInput = z.infer<typeof JoinWaitlistInputSchema>;

export const LeaveWaitlistInputSchema = z.object({
  tenantId: OrgId.optional(),
  waitlistEntryId: z.string().uuid(),
}).strict();
export type LeaveWaitlistInput = z.infer<typeof LeaveWaitlistInputSchema>;

export const CreateProgramInputSchema = z.object({
  tenantId: OrgId.optional(),
  program: CampProgramCreateInputSchema,
  sessions: z.array(CampSessionInputSchema).min(1),
}).strict();
export type CreateProgramInput = z.infer<typeof CreateProgramInputSchema>;

export const UpdateProgramInputSchema = z.object({
  tenantId: OrgId.optional(),
  programId: z.string().uuid(),
  updates: CampProgramUpdateInputSchema,
  sessions: z.array(CampSessionInputSchema).optional(),
}).strict();
export type UpdateProgramInput = z.infer<typeof UpdateProgramInputSchema>;

export const PublishProgramInputSchema = z.object({
  tenantId: OrgId.optional(),
  programId: z.string().uuid(),
}).strict();
export type PublishProgramInput = z.infer<typeof PublishProgramInputSchema>;

export const ArchiveProgramInputSchema = PublishProgramInputSchema;
export type ArchiveProgramInput = z.infer<typeof ArchiveProgramInputSchema>;

export const UpdateSessionCapacityInputSchema = z.object({
  tenantId: OrgId.optional(),
  sessionId: z.string().uuid(),
  capacity: z.number().int().nonnegative(),
}).strict();
export type UpdateSessionCapacityInput = z.infer<typeof UpdateSessionCapacityInputSchema>;

export const PromoteWaitlistEntryInputSchema = z.object({
  tenantId: OrgId.optional(),
  waitlistEntryId: z.string().uuid(),
}).strict();
export type PromoteWaitlistEntryInput = z.infer<typeof PromoteWaitlistEntryInputSchema>;

export const ManualEnrollCamperInputSchema = z.object({
  tenantId: OrgId.optional(),
  programId: z.string().uuid(),
  sessionId: z.string().uuid(),
  studentId: z.string().min(1),
  waivePayment: z.boolean().optional(),
  notes: z.string().optional(),
}).strict();
export type ManualEnrollCamperInput = z.infer<typeof ManualEnrollCamperInputSchema>;

export const RequestRosterExportInputSchema = z.object({
  tenantId: OrgId.optional(),
  programId: z.string().uuid(),
  format: CampRosterExportFormatSchema,
}).strict();
export type RequestRosterExportInput = z.infer<typeof RequestRosterExportInputSchema>;

export const IssueRefundInputSchema = z.object({
  tenantId: OrgId.optional(),
  registrationId: z.string().uuid(),
  amountCents: z.number().int().nonnegative().optional(),
  reason: z.string().optional(),
}).strict();
export type IssueRefundInput = z.infer<typeof IssueRefundInputSchema>;

export const TriggerCampCommunicationInputSchema = z.object({
  tenantId: OrgId.optional(),
  programId: z.string().uuid(),
  audience: z.enum(['registered', 'waitlist', 'all']),
  templateKey: z.string().min(1),
}).strict();
export type TriggerCampCommunicationInput = z.infer<typeof TriggerCampCommunicationInputSchema>;

export const CampActionResultSchema = z.object({
  success: z.boolean(),
  requestId: z.string().uuid(),
  status: z.string().min(1),
}).strict();
export type CampActionResult = z.infer<typeof CampActionResultSchema>;
